<template>
  <div
    :class="{ 'd-none': hidden, 'd-flex': !hidden }"
    class="flex-column flex-grow-1"
    style="height: calc(100vh - 132px);"
  >
    <div
      id="ai-chat-messages"
      ref="messages"
      class="flex-grow-1 card mb-2"
    >
      <div class="card-body">
        <div
          v-if="!isZoomed"
          class="w-100 h-100"
        >
          <div
            v-if="selectedImage"
            class="w-100 h-100 d-flex flex-column justify-content-center align-items-center"
          >
            <p class="h5">
              Selected Image
            </p>
            <img
              :src="selectedImage"
              class="text-removal-selected-image"
              draggable="false"
              @click="handleContinue()"
            >
            <div class="mt-2">
              <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="handleContinue()"
              >
                Continue
              </button>
            </div>
          </div>
          <div
            v-else
            class="w-100 h-100 d-flex flex-column justify-content-center align-items-center"
          >
            <p class="h5">
              Select an Image
            </p>
            <p class="text-muted text-center">
              Please select an image element to remove text from.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Fixed position container for zoomed view -->
    <div
      v-if="isZoomed"
      class="text-removal-container"
      @click.self="handleBack"
    >
      <div class="text-removal-content">
        <div class="text-removal-header">
          <h5 class="text-removal-title">
            Select the area to remove
          </h5>
          <button
            type="button"
            class="close"
            @click="handleBack()"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="text-removal-body">
          <div class="d-flex flex-column gap-3">
            <div 
              class="image-container-wrapper"
              @mousedown="startSelection"
              @mousemove="updateSelection"
              @mouseup="endSelection"
              @mouseleave="endSelection"
              @dragstart.prevent
            >
              <div
                class="image-container"
                :class="{ disabled: isLoading }"
                @dragstart.prevent
              >
                <img
                  ref="zoomedImage"
                  :src="selectedImage"
                  class="zoomed-image"
                  draggable="false"
                >
              </div>
              
              <!-- Overlay for non-selected area -->
              <div
                v-if="hasSelection"
                class="selection-overlay"
                :style="{
                  clipPath: `polygon(
                    0% 0%,
                    0% 100%,
                    100% 100%,
                    100% 0%,
                    0% 0%,
                    ${selectionBox.left}px ${selectionBox.top}px,
                    ${selectionBox.left + selectionBox.width}px ${selectionBox.top}px,
                    ${selectionBox.left + selectionBox.width}px ${selectionBox.top + selectionBox.height}px,
                    ${selectionBox.left}px ${selectionBox.top + selectionBox.height}px,
                    ${selectionBox.left}px ${selectionBox.top}px
                  )`
                }"
              />
              
              <!-- Selection box during selection -->
              <div
                v-if="isSelecting"
                class="selection-box"
                :style="{
                  left: selectionBox.left + 'px',
                  top: selectionBox.top + 'px',
                  width: selectionBox.width + 'px',
                  height: selectionBox.height + 'px'
                }"
              />
              
              <!-- Selected region border -->
              <div
                v-if="hasSelection && !isSelecting"
                class="selected-region-border"
                :style="{
                  left: (selectionBox.left) + 'px',
                  top: (selectionBox.top) + 'px',
                  width: (selectionBox.width) + 'px',
                  height: (selectionBox.height) + 'px'
                }"
              />
              
              <!-- Selection preview -->
              <div
                v-if="hasSelection && !isSelecting"
                class="selection-preview"
              >
                <img
                  v-if="previewImage"
                  :src="previewImage"
                  class="preview-image"
                >
                <div class="preview-info">
                  <p>
                    Size: {{ Math.round(imageSelection.width) }}x{{ Math.round(imageSelection.height) }}px
                    <span 
                      v-if="shouldShowSizeWarning" 
                      v-b-tooltip.hover
                      class="size-warning"
                      title="Selecting an area with a width or height greater than 1024px may result in blurry output."
                    >
                      <i class="fas fa-exclamation-circle" />
                    </span>
                  </p>
                  <p>Position: ({{ Math.round(imageSelection.left) }}, {{ Math.round(imageSelection.top) }})</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="text-removal-footer d-flex flex-column gap-3">
          <div class="form-group w-100">
            <label
              for="promptForEditing"
              class="form-label"
            >AI Removal Prompt</label>
            <textarea 
              id="promptForEditing"
              v-model="promptForEditing"
              class="form-control"
              rows="4"
              placeholder="Enter the prompt for AI text removal..."
            />
          </div>
          <div class="d-flex flex-rowjustify-content-end">
            <button
              type="button"
              class="btn btn-secondary ml-2"
              @click="handleBack()"
            >
              Back
            </button>
            <button
              v-if="imageHistory.length > 1 || (imageHistory.length === 1 && !isLoading)"
              v-b-tooltip.hover
              title="Undo"
              type="button"
              class="btn btn-warning text-white ml-2"
              @click="handleUndo()"
            >
              <svg
                width="16"
                height="16"
                class="btn-icon-svg"
                style="transform: scaleX(-1);"
              >
                <use href="/img/icons/sprite.svg#icon-reset" />
              </svg>
            </button>
            <button
              type="button"
              class="btn btn-primary-outline ml-2"
              :disabled="isLoading"
              @click="handleExpand()"
            >
              <span
                v-if="isLoading && expandInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              {{ isLoading && expandInProgress ? 'Processing...' : (hasSelection ? 'Fill Selection' : 'Expand to fill canvas') }}
            </button>
            <button
              type="button"
              class="btn btn-primary ml-2"
              :disabled="!hasSelection || isLoading"
              @click="handleRemove()"
            >
              <span
                v-if="isLoading && !expandInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              {{ isLoading && !expandInProgress ? 'Processing...' : 'Remove' }}
            </button>
            <button
              v-if="editedImageUrl && (imageHistory.length > 1 || (imageHistory.length === 1 && !isLoading))"
              type="button"
              class="btn btn-success ml-2"
              :disabled="isLoading"
              @click="handleSave()"
            >
              Save Image
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES } from '@frontend/group/modules/additional-fields';
import {
  editorControlEventBroker,
  EVENTS,
} from "@frontend/group/modules/editor-control-popup/event-broker";
import { GLOBAL_UPDATE_LOCAL_STORAGE_KEY } from '@frontend/constants/global-update';
import { deleteURLParameters, setURLParameters } from "@frontend/utils";

export default {
  name: "AiTextRemovalTab",
  props: {
    targetElement: {
      type: fabric.Object,
            require: true,
            default: () => {},
    },
    isTabActive: {
      type: Boolean,
            default: false,
        },
        hidden: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        selectedImage: null,
        isZoomed: false,
        isSelecting: false,
        hasSelection: false,
        previewImage: null,
        editedImageUrl: null,
        selectionBox: {
            left: 0,
            top: 0,
            width: 0,
            height: 0
        },
        imageSelection: {
            left: 0,
            top: 0,
            width: 0,
            height: 0
        },
        startX: 0,
        startY: 0,
        imageStartX: 0,
        imageStartY: 0,
        isLoading: false,
        imageHistory: [], // Array to store previous image states
        containerBounds: null,
        imageBounds: null,
        displayedWidth: null,
        displayedHeight: null,
        offsetX: null,
        offsetY: null,
        scaleX: null,
        scaleY: null,
        expandInProgress: false,
        promptForEditing: 'Remove the main text, lettering, and objects of importance from this image while preserving the exact background, patterns, textures, colors, gradients, and visual elements. Objects of importance include people, humans, animals, prominent objects, logos, and any distinct elements that stand out from the background. IMPORTANT: If humans, people, or faces are present in the selected area, they must be completely removed and replaced with appropriate background content. The edited area must perfectly match the surrounding image in terms of color, texture, and pattern. Do not introduce any new elements or change any existing background elements. The result should look like the text and objects were never there, with the background seamlessly continuing through the previously occupied area. Pay special attention to maintaining visual consistency with the surrounding pixels - ensure smooth color transitions, pattern continuations, and texture matching. Consider the context of the surrounding image to maintain visual coherence. If there are gradients, patterns, or textures in the surrounding area, ensure they continue naturally through the edited region without any visible seams or abrupt changes.'
    }),
    computed: {
        ...mapGetters("layout", ["layers", "target", "selectedInstance"]),
        isDefaultImage() {
            return this.selectedImage?.includes(DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES['additional_background_images']);
        },
        shouldShowSizeWarning() {
            // Calculate original dimensions based on scale
            const originalWidth = this.imageSelection.width * this.scaleX;
            const originalHeight = this.imageSelection.height * this.scaleY;
            return originalWidth > 1024 || originalHeight > 1024;
        }
    },
    watch: {
        isTabActive() {
            this.refreshImage();
        },
        targetElement() {
            this.refreshImage();
        }
    },
    methods: {
        refreshImage() {
            if (!this.targetElement) {
                this.selectedImage = null;
                this.imageHistory = [];
                this.isZoomed = false;
                this.resetSelection();
                return;
            }

            // Check if the selected element is an image
            const isImage = this.targetElement.type === 'image' || 
                (this.targetElement.id && 
                 (this.targetElement.id.includes('image_') || 
                  this.targetElement.id.includes('product_image')));

            if (isImage) {
                // Get the original image source
                let originalSrc = deleteURLParameters(this.targetElement?.getSrc(), ['lightweight', 'full_resolution']);
                originalSrc = setURLParameters(originalSrc, { full_resolution: true });

                // Only reset if the image has actually changed
                if (this.selectedImage !== originalSrc) {
                    this.selectedImage = originalSrc;
                    this.imageHistory = [];
                    this.isZoomed = false;
                    this.resetSelection();
                }
                
                // If it's the default image, treat it as if it's not an image
                if (this.isDefaultImage) {
                    this.selectedImage = null;
                    this.imageHistory = [];
                    this.isZoomed = false;
                    this.resetSelection();
                }
            } else {
                this.selectedImage = null;
                this.imageHistory = [];
                this.isZoomed = false;
                this.resetSelection();
            }
        },
        handleContinue() {
            this.isZoomed = true;
        },
        handleBack() {
            this.isZoomed = false;
            this.resetSelection();
        },
        startSelection(event) {
            if (this.isLoading) return;
            
            const imageRect = this.$refs.zoomedImage.getBoundingClientRect();
            const wrapperRect = event.currentTarget.getBoundingClientRect();
            
            // Store container bounds for later use
            this.containerBounds = wrapperRect;
            this.imageBounds = imageRect;
            
            // Calculate the actual displayed image dimensions based on aspect ratio
            const displayedWidth = imageRect.width;
            const displayedHeight = imageRect.height;

            this.displayedWidth = displayedWidth;
            this.displayedHeight = displayedHeight;

            // Calculate the scaling factor between displayed image and natural image dimensions
            this.scaleX = this.$refs.zoomedImage.naturalWidth / displayedWidth;
            this.scaleY = this.$refs.zoomedImage.naturalHeight / displayedHeight;
            
            // Store image offset from wrapper
            this.offsetX = imageRect.left - wrapperRect.left;
            this.offsetY = imageRect.top - wrapperRect.top;
            
            // Calculate position relative to the wrapper
            const relativeX = event.clientX - wrapperRect.left;
            const relativeY = event.clientY - wrapperRect.top;
            
            // Calculate position relative to the image
            const imageX = relativeX - this.offsetX;
            const imageY = relativeY - this.offsetY;
            
            // Store the container coordinates for visual representation
            this.startX = relativeX;
            this.startY = relativeY;
            
            // Store image coordinates for the actual selection
            this.imageStartX = imageX;
            this.imageStartY = imageY;
            
            this.imageSelection = {
                left: Math.round(Math.max(0, imageX) * this.scaleX),
                top: Math.round(Math.max(0, imageY) * this.scaleY),
                width: 0,
                height: 0
            };
            
            this.isSelecting = true;
            this.hasSelection = false;
            
            this.selectionBox = {
                left: this.startX,
                top: this.startY,
                width: 0,
                height: 0
            };
            
            // Add global mouse event listeners to continue tracking outside container
            document.addEventListener('mousemove', this.handleGlobalMouseMove);
            document.addEventListener('mouseup', this.handleGlobalMouseUp);
        },
        handleGlobalMouseMove(event) {
            if (!this.isSelecting || this.isLoading) return;
            
            // Get the current mouse position relative to wrapper for visual representation
            const currentX = event.clientX - this.containerBounds.left;
            const currentY = event.clientY - this.containerBounds.top;
            
            // Update the selection box visually (this can extend outside the image)
            this.selectionBox = {
                left: Math.min(this.startX, currentX),
                top: Math.min(this.startY, currentY),
                width: Math.abs(currentX - this.startX),
                height: Math.abs(currentY - this.startY)
            };
            
            // Calculate position relative to the image
            const imageCurrentX = currentX - this.offsetX;
            const imageCurrentY = currentY - this.offsetY;
            
            // Calculate image selection based on image boundaries
            const imageLeft = Math.max(0, Math.min(this.imageStartX, imageCurrentX));
            const imageTop = Math.max(0, Math.min(this.imageStartY, imageCurrentY));
            
            // Clamp the right and bottom to image dimensions
            const imageRight = Math.min(this.displayedWidth, Math.max(this.imageStartX, imageCurrentX));
            const imageBottom = Math.min(this.displayedHeight, Math.max(this.imageStartY, imageCurrentY));
            
            // Update image coordinates for actual selection
            this.imageSelection = {
                left: Math.round(imageLeft * this.scaleX),
                top: Math.round(imageTop * this.scaleY),
                width: Math.round((imageRight - imageLeft) * this.scaleX),
                height: Math.round((imageBottom - imageTop) * this.scaleY)
            };
            
            // If selection is valid (has width and height), set hasSelection to true
            this.hasSelection = this.imageSelection.width > 0 && this.imageSelection.height > 0;
            
            // Generate preview of selected region without requiring mouse release
            if (this.hasSelection) {
                this.generatePreview();
            }
        },
        updateSelection(event) {
            if (!this.isSelecting || this.isLoading) return;
            // This is still needed for direct container events
            this.handleGlobalMouseMove(event);
        },
        handleGlobalMouseUp() {
            this.endSelection();
        },
        endSelection() {
            if (!this.isSelecting || this.isLoading) return;
            
            this.isSelecting = false;
            
            // Remove global event listeners
            document.removeEventListener('mousemove', this.handleGlobalMouseMove);
            document.removeEventListener('mouseup', this.handleGlobalMouseUp);
        },
        generatePreview() {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Ensure selection dimensions are valid
                if (this.imageSelection.width <= 0 || this.imageSelection.height <= 0) {
                    this.previewImage = null;
                    return;
                }
                
                // Use the actual image dimensions for accurate cropping
                canvas.width = this.imageSelection.width;
                canvas.height = this.imageSelection.height;
                
                // Clear the canvas with transparency
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                try {
                    ctx.drawImage(
                        img,
                        this.imageSelection.left,
                        this.imageSelection.top,
                        this.imageSelection.width,
                        this.imageSelection.height,
                        0,
                        0,
                        this.imageSelection.width,
                        this.imageSelection.height
                    );

                    this.previewImage = canvas.toDataURL('image/png');
                } catch(e) {
                    console.error('Error generating preview:', e);
                    this.previewImage = null;
                }
            };

            img.src = this.selectedImage;
        },
        resetSelection() {
            this.isSelecting = false;
            this.hasSelection = false;
            this.previewImage = null;
            this.selectionBox = {
                left: 0,
                top: 0,
                width: 0,
                height: 0
            };
            this.imageSelection = {
                left: 0,
                top: 0,
                width: 0,
                height: 0
            };
        },
        handleRemove() {
            if (!this.hasSelection || !this.selectedImage) return;

            // Store current image state temporarily
            const currentImageState = this.selectedImage;

            this.isLoading = true;

            // Create a temporary canvas to crop the selected region
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                // Validate image dimensions
                if (this.imageSelection.width <= 0 || this.imageSelection.height <= 0) {
                    alert('Please make a proper selection before removing an area.');
                    this.isLoading = false;
                    return;
                }

                // Ensure selection is within the image boundaries
                const clippedSelection = {
                    left: Math.max(0, Math.min(this.imageSelection.left, img.width)),
                    top: Math.max(0, Math.min(this.imageSelection.top, img.height)),
                    width: Math.min(this.imageSelection.width, img.width - Math.max(0, this.imageSelection.left)),
                    height: Math.min(this.imageSelection.height, img.height - Math.max(0, this.imageSelection.top))
                };

                // Calculate padded selection (40% padding on each side for more context)
                const padding = 0.4; // 40% padding
                const paddedWidth = Math.round(clippedSelection.width * (1 + padding * 2));
                const paddedHeight = Math.round(clippedSelection.height * (1 + padding * 2));
                
                // Calculate padding while keeping the selection centered
                const paddingX = Math.round(clippedSelection.width * padding);
                const paddingY = Math.round(clippedSelection.height * padding);
                
                // Calculate the padded coordinates while keeping the selection centered
                const paddedLeft = Math.max(0, Math.round(clippedSelection.left - paddingX));
                const paddedTop = Math.max(0, Math.round(clippedSelection.top - paddingY));
                
                // Adjust if padding would go beyond image boundaries
                const adjustedPaddedLeft = Math.min(paddedLeft, Math.max(0, img.width - paddedWidth));
                const adjustedPaddedTop = Math.min(paddedTop, Math.max(0, img.height - paddedHeight));

                // Calculate the actual padding used after boundary adjustments
                const actualPaddingX = Math.round(clippedSelection.left - adjustedPaddedLeft);
                const actualPaddingY = Math.round(clippedSelection.top - adjustedPaddedTop);

                // Set canvas size to match padded selection
                canvas.width = paddedWidth;
                canvas.height = paddedHeight;

                // Clear the canvas with transparency
                ctx.clearRect(0, 0, paddedWidth, paddedHeight);

                // Draw the padded region
                ctx.drawImage(
                    img,
                    adjustedPaddedLeft,
                    adjustedPaddedTop,
                    paddedWidth,
                    paddedHeight,
                    0,
                    0,
                    paddedWidth,
                    paddedHeight
                );

                // Create a metadata object to better describe the task to the AI
                const selectionMetadata = {
                    originalWidth: img.width,
                    originalHeight: img.height,
                    selectionX: clippedSelection.left - adjustedPaddedLeft,
                    selectionY: clippedSelection.top - adjustedPaddedTop,
                    selectionWidth: clippedSelection.width,
                    selectionHeight: clippedSelection.height,
                    paddedWidth: paddedWidth,
                    paddedHeight: paddedHeight
                };

                // Draw a subtle highlight around the selection area to help the AI
                // (This will be removed in the final result)
                const highlightCtx = canvas.getContext('2d');
                highlightCtx.strokeStyle = 'rgba(255, 255, 255, 0.3)';  
                highlightCtx.lineWidth = 1;
                highlightCtx.strokeRect(
                    actualPaddingX, 
                    actualPaddingY, 
                    clippedSelection.width, 
                    clippedSelection.height
                );

                // Convert canvas to blob
                canvas.toBlob(async (blob) => {
                    if (!blob) {
                        alert('Failed to process image selection. Please try again.');
                        this.isLoading = false;
                        return;
                    }

                    // Validate dimensions
                    if (paddedWidth <= 0 || paddedHeight <= 0 || 
                        !Number.isInteger(paddedWidth) || !Number.isInteger(paddedHeight)) {
                        alert('Invalid image dimensions. Please try again.');
                        this.isLoading = false;
                        return;
                    }

                    const formData = new FormData();
                    // Convert the blob to a File object
                    const file = new File([blob], 'selected_region.png', { type: blob.type });
                    formData.append('image', file);
                    formData.append('width', paddedWidth.toString());
                    formData.append('height', paddedHeight.toString());
                    formData.append('originalSelection', JSON.stringify({
                        left: actualPaddingX,
                        top: actualPaddingY,
                        width: Math.round(clippedSelection.width),
                        height: Math.round(clippedSelection.height),
                        metadata: selectionMetadata
                    }));
                    formData.append('promptForEditing', this.promptForEditing);

                    try {
                        const response = await axios.post('/ai-edit-image', formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        });

                        if (!response.data || !response.data.url) {
                            throw new Error('Invalid response from server');
                        }

                        // Create a new image with the edited region
                        const editedImg = new Image();
                        editedImg.onload = () => {
                            // Create a new canvas with the original image
                            const fullCanvas = document.createElement('canvas');
                            const fullCtx = fullCanvas.getContext('2d');
                            fullCanvas.width = img.width;
                            fullCanvas.height = img.height;

                            // Draw the original image
                            fullCtx.drawImage(img, 0, 0);

                            // Create a mask from the original image's alpha channel
                            const maskCanvas = document.createElement('canvas');
                            const maskCtx = maskCanvas.getContext('2d');
                            maskCanvas.width = clippedSelection.width;
                            maskCanvas.height = clippedSelection.height;

                            // Draw the original selection to get its alpha channel
                            maskCtx.drawImage(
                                img,
                                clippedSelection.left,
                                clippedSelection.top,
                                clippedSelection.width,
                                clippedSelection.height,
                                0,
                                0,
                                clippedSelection.width,
                                clippedSelection.height
                            );

                            // Get the alpha channel data
                            const maskData = maskCtx.getImageData(0, 0, clippedSelection.width, clippedSelection.height);
                            const maskPixels = maskData.data;

                            // Create a new canvas for the edited image with transparency
                            const editedCanvas = document.createElement('canvas');
                            const editedCtx = editedCanvas.getContext('2d');
                            editedCanvas.width = clippedSelection.width;
                            editedCanvas.height = clippedSelection.height;

                            // Draw the edited image
                            editedCtx.drawImage(
                                editedImg,
                                actualPaddingX,
                                actualPaddingY,
                                clippedSelection.width,
                                clippedSelection.height,
                                0,
                                0,
                                clippedSelection.width,
                                clippedSelection.height
                            );

                            // Get the edited image data
                            const editedData = editedCtx.getImageData(0, 0, clippedSelection.width, clippedSelection.height);
                            const editedPixels = editedData.data;

                            // Apply the original alpha channel to the edited image
                            for (let i = 0; i < maskPixels.length; i += 4) {
                                editedPixels[i + 3] = maskPixels[i + 3]; // Copy alpha channel
                            }

                            // Put the modified image data back
                            editedCtx.putImageData(editedData, 0, 0);

                            // Draw the edited region back in place with preserved transparency
                            fullCtx.drawImage(
                                editedCanvas,
                                0,
                                0,
                                clippedSelection.width,
                                clippedSelection.height,
                                clippedSelection.left,
                                clippedSelection.top,
                                clippedSelection.width,
                                clippedSelection.height
                            );

                            // Convert back to data URL
                            const editedImageUrl = fullCanvas.toDataURL('image/png');
                            
                            // Only add to history after successful processing
                            this.imageHistory.push(currentImageState);
                            
                            // Update the selected image and store the edited URL
                            this.selectedImage = editedImageUrl;
                            this.editedImageUrl = editedImageUrl;

                            // Reset selection
                            this.resetSelection();
                            this.isLoading = false;
                        };
                        editedImg.onerror = () => {
                            alert('Failed to load edited image. Please try again.');
                            this.isLoading = false;
                        };
                        editedImg.src = window.location.origin + response.data.url;
                    } catch (error) {
                        console.error('Error editing image:', error);
                        alert('There was an error processing the image. Please try again.');
                        this.isLoading = false;
                    }
                }, 'image/png');
            };

            img.onerror = () => {
                alert('Failed to load the image. Please try again.');
                this.isLoading = false;
            };

            img.src = this.selectedImage;
        },
        handleSave() {
            if (!this.editedImageUrl) {
                alert('No changes to save. Please remove text first.');
                return;
            }

            // Convert data URL to blob
            fetch(this.editedImageUrl)
                .then(response => response.blob())
                .then(blob => {
                    const now = new Date();
                    const timestamp = now.toISOString().replace(/[:.]/g, "-");
                    const filename = `image-${timestamp}.png`;
                    const file = new File([blob], filename, { type: blob.type });

                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(file);

                    editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
                        action: "selectBackground",
                        value: undefined,
                    });

                    const droppedFiles = dataTransfer.files;
                    const $newBackgroundImage = $("#new-background-image");
                    const $selectBkImgModal = $("#selectBkImgModal");
                    const $buttonSelect = $("#selectBkImgModal #submit");
                    const $compressCheckbox = $("#compress_check");
                    const $fileCaption = $newBackgroundImage
                        .closest(".input-group")
                        .find(".file-caption-name");

                    $newBackgroundImage.fileinput("readFiles", droppedFiles);
                    $newBackgroundImage[0].files = droppedFiles;

                    $fileCaption.val(`${droppedFiles.length} file selected`);
                    $fileCaption.addClass("is-valid");

                    if (droppedFiles.length) {
                        $compressCheckbox.prop("checked", true);

                        $buttonSelect.hasClass("active")
                            ? $buttonSelect.removeClass("active")
                            : $buttonSelect.addClass("active");

                        $selectBkImgModal.find("#upload").show();
                        $selectBkImgModal.find(".compress_check, .crop_check").show();
                    } else {
                        $selectBkImgModal.find("#upload").hide();
                        $selectBkImgModal.find(".compress_check, .crop_check").hide();
                    }

                    localStorage.setItem(GLOBAL_UPDATE_LOCAL_STORAGE_KEY, false);

                    this.$root.$emit("productImageReplace", {
                        dataTransfer,
                    });
                });
        },
        handleUndo() {
            if (this.imageHistory.length > 0) {
                this.selectedImage = this.imageHistory.pop();
                this.resetSelection();
            }
        },
        handleExpand() {
          if (this.isLoading) return;

          // Store current image state temporarily
          const currentImageState = this.selectedImage;

          this.isLoading = true;
          this.expandInProgress = true;

          if (!this.hasSelection) {
            // No selection - expand the image to fill the template canvas
            this.expandToFillCanvas(currentImageState);
            return;
          }

          // Rest of the existing code for handling selection-based expansion
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();

          img.onload = () => {
            // Validate image dimensions
            if (this.imageSelection.width <= 0 || this.imageSelection.height <= 0) {
              alert('Please make a proper selection before expanding the image.');
              this.isLoading = false;
              this.expandInProgress = false;
              return;
            }

            // Ensure selection is within the image boundaries
            const clippedSelection = {
              left: Math.max(0, Math.min(this.imageSelection.left, img.width)),
              top: Math.max(0, Math.min(this.imageSelection.top, img.height)),
              width: Math.min(this.imageSelection.width, img.width - Math.max(0, this.imageSelection.left)),
              height: Math.min(this.imageSelection.height, img.height - Math.max(0, this.imageSelection.top))
            };

            // Calculate padded selection (40% padding on each side for more context)
            const padding = 0.4; // 40% padding
            const paddedWidth = Math.round(clippedSelection.width * (1 + padding * 2));
            const paddedHeight = Math.round(clippedSelection.height * (1 + padding * 2));

            // Calculate padding while keeping the selection centered
            const paddingX = Math.round(clippedSelection.width * padding);
            const paddingY = Math.round(clippedSelection.height * padding);

            // Calculate the padded coordinates while keeping the selection centered
            const paddedLeft = Math.max(0, Math.round(clippedSelection.left - paddingX));
            const paddedTop = Math.max(0, Math.round(clippedSelection.top - paddingY));

            // Adjust if padding would go beyond image boundaries
            const adjustedPaddedLeft = Math.min(paddedLeft, Math.max(0, img.width - paddedWidth));
            const adjustedPaddedTop = Math.min(paddedTop, Math.max(0, img.height - paddedHeight));

            // Calculate the actual padding used after boundary adjustments
            const actualPaddingX = Math.round(clippedSelection.left - adjustedPaddedLeft);
            const actualPaddingY = Math.round(clippedSelection.top - adjustedPaddedTop);

            // Set canvas size to match padded selection
            canvas.width = paddedWidth;
            canvas.height = paddedHeight;

            // Clear the canvas with transparency
            ctx.clearRect(0, 0, paddedWidth, paddedHeight);

            // Draw the padded region
            ctx.drawImage(
              img,
              adjustedPaddedLeft,
              adjustedPaddedTop,
              paddedWidth,
              paddedHeight,
              0,
              0,
              paddedWidth,
              paddedHeight
            );

            // Create a metadata object to better describe the task to the AI
            const selectionMetadata = {
              originalWidth: img.width,
              originalHeight: img.height,
              selectionX: clippedSelection.left - adjustedPaddedLeft,
              selectionY: clippedSelection.top - adjustedPaddedTop,
              selectionWidth: clippedSelection.width,
              selectionHeight: clippedSelection.height,
              paddedWidth: paddedWidth,
              paddedHeight: paddedHeight
            };

            // Draw a subtle highlight around the selection area to help the AI
            // (This will be removed in the final result)
            const highlightCtx = canvas.getContext('2d');
            highlightCtx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
            highlightCtx.lineWidth = 1;
            highlightCtx.strokeRect(
              actualPaddingX,
              actualPaddingY,
              clippedSelection.width,
              clippedSelection.height
            );

            // Convert canvas to blob
            canvas.toBlob(async (blob) => {
              if (!blob) {
                alert('Failed to process image selection. Please try again.');
                this.isLoading = false;
                this.expandInProgress = false;
                return;
              }

              // Validate dimensions
              if (paddedWidth <= 0 || paddedHeight <= 0 ||
                !Number.isInteger(paddedWidth) || !Number.isInteger(paddedHeight)) {
                alert('Invalid image dimensions. Please try again.');
                this.isLoading = false;
                this.expandInProgress = false;
                return;
              }

              const formData = new FormData();
              // Convert the blob to a File object
              const file = new File([blob], 'selected_region.png', { type: blob.type });
              formData.append('image', file);
              formData.append('width', paddedWidth.toString());
              formData.append('height', paddedHeight.toString());
              formData.append('originalSelection', JSON.stringify({
                left: actualPaddingX,
                top: actualPaddingY,
                width: Math.round(clippedSelection.width),
                height: Math.round(clippedSelection.height),
                metadata: selectionMetadata
              }));
              formData.append('promptForEditing', 'Infill: Expand the visible content to fill the entire selected area. Use surrounding pixels to intelligently fill any empty, transparent, or solid color regions with appropriate texture and pattern that matches the rest of the image. Maintain consistent lighting, color tones, and texture direction. The goal is to make it look like the empty areas were never there by extending the existing background content naturally. Do not alter or modify the existing visible content - only fill empty spaces.');

              try {
                const response = await axios.post('/ai-edit-image', formData, {
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                });

                if (!response.data || !response.data.url) {
                  throw new Error('Invalid response from server');
                }

                // Create a new image with the edited region
                const editedImg = new Image();
                editedImg.onload = () => {
                  // Create a new canvas with the original image
                  const fullCanvas = document.createElement('canvas');
                  const fullCtx = fullCanvas.getContext('2d');
                  fullCanvas.width = img.width;
                  fullCanvas.height = img.height;

                  // Draw the original image
                  fullCtx.drawImage(img, 0, 0);

                  // Create a mask from the original image's alpha channel
                  const maskCanvas = document.createElement('canvas');
                  const maskCtx = maskCanvas.getContext('2d');
                  maskCanvas.width = clippedSelection.width;
                  maskCanvas.height = clippedSelection.height;

                  // Draw the original selection to get its alpha channel
                  maskCtx.drawImage(
                    img,
                    clippedSelection.left,
                    clippedSelection.top,
                    clippedSelection.width,
                    clippedSelection.height,
                    0,
                    0,
                    clippedSelection.width,
                    clippedSelection.height
                  );

                  // Get the alpha channel data
                  const maskData = maskCtx.getImageData(0, 0, clippedSelection.width, clippedSelection.height);
                  const maskPixels = maskData.data;

                  // Create a new canvas for the edited image with transparency
                  const editedCanvas = document.createElement('canvas');
                  const editedCtx = editedCanvas.getContext('2d');
                  editedCanvas.width = clippedSelection.width;
                  editedCanvas.height = clippedSelection.height;

                  // Draw the edited image
                  editedCtx.drawImage(
                    editedImg,
                    actualPaddingX,
                    actualPaddingY,
                    clippedSelection.width,
                    clippedSelection.height,
                    0,
                    0,
                    clippedSelection.width,
                    clippedSelection.height
                  );

                  // Get the edited image data
                  const editedData = editedCtx.getImageData(0, 0, clippedSelection.width, clippedSelection.height);
                  const editedPixels = editedData.data;

                  // Apply the original alpha channel to the edited image
                  for (let i = 0; i < maskPixels.length; i += 4) {
                    editedPixels[i + 3] = maskPixels[i + 3]; // Copy alpha channel
                  }

                  // Put the modified image data back
                  editedCtx.putImageData(editedData, 0, 0);

                  // Draw the edited region back in place with preserved transparency
                  fullCtx.drawImage(
                    editedCanvas,
                    0,
                    0,
                    clippedSelection.width,
                    clippedSelection.height,
                    clippedSelection.left,
                    clippedSelection.top,
                    clippedSelection.width,
                    clippedSelection.height
                  );

                  // Convert back to data URL
                  const editedImageUrl = fullCanvas.toDataURL('image/png');

                  // Only add to history after successful processing
                  this.imageHistory.push(currentImageState);

                  // Update the selected image and store the edited URL
                  this.selectedImage = editedImageUrl;
                  this.editedImageUrl = editedImageUrl;

                  // Reset selection
                  this.resetSelection();
                  this.isLoading = false;
                  this.expandInProgress = false;
                };
                editedImg.onerror = () => {
                  alert('Failed to load edited image. Please try again.');
                  this.isLoading = false;
                  this.expandInProgress = false;
                };
                editedImg.src = window.location.origin + response.data.url;
              } catch (error) {
                console.error('Error editing image:', error);
                alert('There was an error processing the image. Please try again.');
                this.isLoading = false;
                this.expandInProgress = false;
              }
            }, 'image/png');
          };

          img.onerror = () => {
            alert('Failed to load the image. Please try again.');
            this.isLoading = false;
            this.expandInProgress = false;
          };

          img.src = this.selectedImage;
        },
        expandToFillCanvas(currentImageState) {
          // Get canvas dimensions from the targetElement
          if (!this.targetElement || !this.targetElement.canvas) {
            this.isLoading = false;
            this.expandInProgress = false;
            alert('Cannot determine canvas dimensions.');
            return;
          }

          // Load the image to determine its dimensions
          const img = new Image();
          img.onload = () => {
            // Get template canvas dimensions - use the canvas dimensions of the target element
            const canvasWidth = this.targetElement.canvas.width;
            const canvasHeight = this.targetElement.canvas.height;
            
            // Original image dimensions
            const imgWidth = img.width;
            const imgHeight = img.height;
            
            // Calculate the new dimensions
            let newWidth = canvasWidth;
            let newHeight = canvasHeight;
            
            // If image width is larger than canvas width, don't change it
            if (imgWidth > canvasWidth) {
              newWidth = imgWidth;
            }
            
            // If image height is larger than canvas height, don't change it
            if (imgHeight > canvasHeight) {
              newHeight = imgHeight;
            }
            
            // Create a new canvas with the new dimensions
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = newWidth;
            canvas.height = newHeight;
            
            // Fill with transparent background
            ctx.clearRect(0, 0, newWidth, newHeight);
            
            // Calculate position to center the image
            const x = Math.max(0, (newWidth - imgWidth) / 2);
            const y = Math.max(0, (newHeight - imgHeight) / 2);
            
            // Draw the image centered in the new canvas
            ctx.drawImage(img, x, y);
            
            // Convert to data URL
            const editedImageUrl = canvas.toDataURL('image/png');
            
            // Add to history and update the image
            this.imageHistory.push(currentImageState);
            this.selectedImage = editedImageUrl;
            this.editedImageUrl = editedImageUrl;
            
            // Reset and finish
            this.resetSelection();
            this.isLoading = false;
            this.expandInProgress = false;
          };
          
          img.onerror = () => {
            alert('Failed to load the image. Please try again.');
            this.isLoading = false;
            this.expandInProgress = false;
          };
          
          img.src = this.selectedImage;
        },
    },
};
</script>

<style scoped lang="scss">
.chat-module {
    height: calc(100vh - 98px);
}
.chat-module .card {
    position: relative;
    overflow-y: auto;
}
.message {
    margin: 10px;
    padding: 10px;
    border-radius: 10px;
}
.message p {
    margin-bottom: 0px;
}
.message-user {
    background-color: #209c86;
    color: white;
    text-align: right;
}
.message-assistant {
    background-color: #e9ecef;
    text-align: left;
}
.image-container {
    position: relative;
    display: inline-block;
    text-align: center;
    overflow: hidden;
    margin: 0 auto;
    max-width: 100%;
}
.image-container-wrapper {
  border: 1px solid #209c86;
  flex-grow: 1;
  margin: auto;
  width: 100%;
  position: relative;
  cursor: crosshair;
  display: flex;
  justify-content: center;
  align-items: center;
}
.image-container.disabled {
    cursor: not-allowed;
    opacity: 0.7;
}
.selection-box {
    position: absolute;
    border: 2px dashed #209c86;
    background-color: rgba(32, 156, 134, 0.1);
    pointer-events: none;
    z-index: 5;
}

.selected-region-border {
    position: absolute;
    border: 2px dashed #209c86;
    pointer-events: none;
    z-index: 5;
}

.selection-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    pointer-events: none;
    z-index: 4;
}

.text-removal-container {
    width: calc(65vw - 9px);
    height: fit-content;
    position: fixed;
    background: rgb(255, 255, 255);
    z-index: 101;
    top: 87px;
    right: 64px;
    border: 1px solid;
    box-shadow: 2px 2px #888888;
    overflow-y: auto;
    padding: 20px;
    cursor: pointer;
}

.text-removal-content {
    cursor: default;
}

.text-removal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.text-removal-title {
    margin: 0;
}

.text-removal-body {
    max-height: 50vh;
}

.text-removal-footer {
    display: flex;
    gap: 10px;
    margin-top: 15px;
    justify-content: flex-end;
}

@media screen and (max-height: 730px) {
    .text-removal-body {
        max-height: 400px;
    }
    
    .text-removal-container {
        height: 500px;
    }
}

.text-removal-selected-image {
  width: 300px;
  height: 300px;
  object-fit: contain;
  cursor: pointer;
}

.text-removal-selected-image:hover {
  border: 2px solid #209c86;
}

.zoomed-image {
    width: 100%;
    height: auto;
    max-height: 50vh;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
    cursor: crosshair;
    display: block;
    margin: 0 auto;
    -webkit-user-drag: none;
}

.image-container * {
    user-select: none;
    -webkit-user-drag: none;
}

.selection-preview {
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 200px;
  height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 5px;
  padding: 10px;
  user-select: none;
  z-index: 102;
}

.preview-image {
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
  margin-bottom: 5px;
}

.preview-info {
  color: white;
  font-size: 12px;
  text-align: center;
  width: 100%;
}

.preview-info p {
  margin-bottom: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.size-warning {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffcc00;
  margin-left: 5px;
  font-size: 14px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 50%;
  width: 18px;
  height: 18px;
}

.size-warning i {
  vertical-align: middle;
  line-height: 1;
}

.btn-primary-outline {
  color: #209c86;
  background: transparent;
  border: 1px solid #209c86;
}
.btn-primary-outline:hover:not(:disabled) {
  color: white;
  background: #209c86;
}
.btn-primary-outline:disabled {
  color: #6c757d;
  border-color: #6c757d;
}
</style>